import { AutoloadClient, DataSource } from "./DataSource";
import {
  PropertyTranslator,
  IGenericRequestParametersTranslator,
  IPaginationParametersTranslator,
  INamedEntityParametersTranslator,
  INamedEntityLabels,
  IEntryRecordLabels,
  IModificationRecordLabels,
  IUniqueEntityLabels,
  IUniqueEntityParametersTranslator,
  IModifiedRecordParametersTranslator,
  IEntryRecordParametersTranslator,
} from "./GenericTranslator";
import {
  EntryRecordOrderBy,
  ModificationRecordOrderBy,
  GenericEntityOrderBy,
  EntityConstants,
  ICreatedRecordParameters,
  IEntityMinimalModel,
  IEntryRecord,
  IGenericRequestParameters,
  IModelWithIntId,
  IModificationRecord,
  IModifiedRecordParameters,
  INamedEntity,
  INamedEntityParameters,
  IPaginationParameters,
  IPermissionedEntity,
  IRelatedEntity,
  IRelation,
  IRelationModel,
  IUniqueEntity,
  IUniqueEntityParameters,
  NamedEntityOrderBy,
} from "./GenericTypes";

// Autoload source
export const bridgeConstants: EntityConstants<Bridge, BridgeFilters> = {
  resource: "bridges",
  frontendIndexRoute: "bridges",
  entitySingular: "bridge",
  entityPlural: "bridges",
  icon: "unplug",
};

export const BridgeFieldLabels: PropertyTranslator<Bridge> & {
  operatingSystem: string;
  executablePath: string;
  clientVersion: string;
  datasets: string;
} = {
  ...INamedEntityLabels,
  ...IEntryRecordLabels,
  ...IModificationRecordLabels,
  ...IUniqueEntityLabels,
  id: "Bridge ID",
  type: "Connection type",
  hostname: "Host name / IP address",
  port: "Port",
  ipAddress: "IP address",
  isConnected: "Status",
  username: "User name",
  password: "Password",
  privateKey: "Private key",
  description: "Description",
  areMultipleClientsConnected: "Multiple clients connected",
  connectedClients: "Connected clients",
  permissions: "Permissions",
  relations: "Relations",
  sftpAuthenticationMethod: "Authentication method",
  operatingSystem: "Operating system",
  executablePath: "Executable path",
  clientVersion: "Client version",
  datasets: "Datasets",
  dataSources: "Data sources",
} as const;

export const BridgeValidationOptions = [
  { id: "Password" as BridgeValidationOption, name: "Password" },
  { id: "PrivateKey" as BridgeValidationOption, name: "SSH private key" },
];

export type BridgeType = "Unknown" | "Client" | "SFTP";
export const BridgeTypes = ["Unknown", "Client", "SFTP"] as BridgeType[];
export const BridgeTypeFormValues = BridgeTypes.map((t) => ({ id: t, name: t }));
export type BridgeValidationOption = "PrivateKey" | "Password";
export interface Bridge
  extends
    IPermissionedEntity,
    IModelWithIntId,
    INamedEntity,
    IRelatedEntity<BridgeRelations>,
    IUniqueEntity,
    IEntryRecord,
    IModificationRecord {
  type: BridgeType;
  hostname: string;
  ipAddress: string;
  description: string;
  username?: string;
  port?: number;
  password?: string;
  privateKey?: string;
  connectedClients: AutoloadClient[];
  dataSources: DataSource[];
  isConnected?: boolean;
  areMultipleClientsConnected: boolean;
  sftpAuthenticationMethod?: BridgeValidationOption;
}

export interface BridgeRelations extends IRelationModel {
  datasets: IRelation;
  dataSources: IRelation;
}

export interface BridgeSuggestions extends IEntityMinimalModel<Bridge["id"]> {
  type: BridgeType;
  ipAddress: string;
  hostname: string;
}

export interface BridgeFilters
  extends
    IGenericRequestParameters<Bridge, BridgeSortingOptions>,
    IPaginationParameters,
    INamedEntityParameters,
    ICreatedRecordParameters,
    IModifiedRecordParameters,
    IUniqueEntityParameters {
  hostnames?: string[] | null;
  usernames?: string[] | null;
  ipAddresses?: string[] | null;
  dataSourceIds?: number[] | null;
  types?: BridgeType[] | null;
  datasetIds?: number[] | null;
  isConfigured?: boolean | null;
}

export const BridgeFiltersTranslator: PropertyTranslator<BridgeFilters> = {
  ...IPaginationParametersTranslator,
  ...IGenericRequestParametersTranslator,
  ...INamedEntityParametersTranslator,
  ...IEntryRecordParametersTranslator,
  ...IModifiedRecordParametersTranslator,
  ...IUniqueEntityParametersTranslator,
  hostnames: "Host names",
  usernames: "User names",
  ipAddresses: "IP-Addresses",
  dataSourceIds: "Data source IDs",
  types: "Bridge types",
  datasetIds: "Datasets",
  isConfigured: "Is configured",
} as const;

export const BridgeSortingOptionsConsts = [
  ...GenericEntityOrderBy,
  ...NamedEntityOrderBy,
  ...EntryRecordOrderBy,
  ...ModificationRecordOrderBy,
  "TYPE",
] as const;

export type BridgeSortingOptions = (typeof BridgeSortingOptionsConsts)[number];
